import React from 'react';
import { ExternalLink, Link as LinkIcon } from 'lucide-react';
import { sanitizeText } from '../lib/sanitize';

// Triângulo de "play" preenchido (o Play do lucide é contornado).
function PlayGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 5.5v13l11-6.5-11-6.5Z" />
    </svg>
  );
}

// Lista/grade de itens {thumb|ícone, título, subtítulo, url}, com cabeçalho
// (olho-d'água + título) e botão de rodapé opcionais. Cobre "canções",
// "playlists", "redes" etc. Reutilizável.
// config: { layout:'list'|'grid', media:'thumb'|'icon'|'none', columns:2|3,
//           rowIcon:'open'|'play'|'none', heading:{eyebrow,title},
//           footer:{label,url}, items:[...] }
// iconMap: mapa nome->componente de ícone (passe o ICON_MAP do projeto).
export default function BlockMediaList({ config, onTrack, iconMap = {} }) {
  const { layout = 'list', media = 'thumb', columns = 3, rowIcon = 'open', heading = {}, footer = {}, items = [] } = config;
  const valid = (items || []).filter((it) => it && (it.title || it.subtitle || it.url || it.image || it.icon));
  const hasHeading = heading && (heading.eyebrow || heading.title);
  if (!valid.length && !hasHeading) return null;
  // Mobile = 1 coluna sempre (igual ao original); só a partir de sm vira grade.
  const gridCols = columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3';

  const Media = ({ item, round }) => {
    if (media === 'thumb') {
      const box = round ? 'w-11 h-11 rounded-full' : 'h-12 w-20 rounded-md';
      return item.image ? (
        <img src={sanitizeText(item.image)} alt="" loading="lazy" className={`object-cover shrink-0 ${box}`} />
      ) : (
        <span className={`shrink-0 bg-surface dark:bg-surface-dark ${box}`} />
      );
    }
    if (media === 'icon') {
      const Ico = iconMap[item.icon] || LinkIcon;
      return (
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-surface dark:bg-surface-dark text-action dark:text-action-dark">
          <Ico className="w-5 h-5" />
        </span>
      );
    }
    return null;
  };

  // Linha: título em cima (largura toda), embaixo thumb/ícone + meta e o play na direita.
  const Row = ({ item }) => {
    const showLine2 = media !== 'none' || item.subtitle || rowIcon !== 'none';
    const inner = (
      <div className="min-w-0">
        <span className="block text-sm font-semibold text-text-primary dark:text-text-primary-dark leading-tight">
          {sanitizeText(item.title || '')}
        </span>
        {showLine2 && (
          <div className="mt-2 flex items-center gap-3">
            {media !== 'none' && <Media item={item} round={false} />}
            {item.subtitle && (
              <span className="min-w-0 flex-1 truncate text-sm text-text-muted dark:text-text-muted-dark">
                {sanitizeText(item.subtitle)}
              </span>
            )}
            {!item.subtitle && <span className="flex-1" />}
            {rowIcon === 'play' && <PlayGlyph className="w-4 h-4 shrink-0 text-action dark:text-action-dark" />}
            {rowIcon === 'open' && item.url && <ExternalLink className="w-4 h-4 shrink-0 text-action dark:text-action-dark" />}
          </div>
        )}
      </div>
    );
    const cls = 'block rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark p-3.5 transition-colors hover:bg-surface dark:hover:bg-surface-dark';
    return item.url ? (
      <a href={sanitizeText(item.url)} target="_blank" rel="noreferrer" onClick={() => onTrack && onTrack('media_list_click', { title: item.title, url: item.url })} className={cls}>
        {inner}
      </a>
    ) : (
      <div className={cls}>{inner}</div>
    );
  };

  const Card = ({ item }) => {
    const inner = (
      <>
        {media !== 'none' && <Media item={item} round />}
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-text-primary dark:text-text-primary-dark leading-tight">{sanitizeText(item.title || '')}</span>
          {item.subtitle && <span className="block text-sm text-text-muted dark:text-text-muted-dark mt-0.5 leading-snug">{sanitizeText(item.subtitle)}</span>}
        </span>
      </>
    );
    const cls = 'flex items-center gap-4 p-4 rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark transition-colors hover:bg-surface dark:hover:bg-surface-dark sm:flex-col sm:items-start';
    return item.url ? (
      <a href={sanitizeText(item.url)} target="_blank" rel="noreferrer" onClick={() => onTrack && onTrack('media_list_click', { title: item.title, url: item.url })} className={cls}>
        {inner}
      </a>
    ) : (
      <div className={cls}>{inner}</div>
    );
  };

  return (
    <div>
      {hasHeading && (
        <div className="mb-3">
          {heading.eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.25em] text-action dark:text-action-dark">{sanitizeText(heading.eyebrow)}</p>}
          {heading.title && (
            <h3 className="text-xl text-text-primary dark:text-text-primary-dark mt-1" style={{ fontFamily: 'var(--font-display, inherit)' }}>
              {sanitizeText(heading.title)}
            </h3>
          )}
        </div>
      )}
      {layout === 'grid' ? (
        <div className={`grid grid-cols-1 ${gridCols} gap-3`}>
          {valid.map((it, i) => <Card key={i} item={it} />)}
        </div>
      ) : (
        <div className="grid gap-3">
          {valid.map((it, i) => <Row key={i} item={it} />)}
        </div>
      )}
      {footer && footer.label && footer.url && (
        <a
          href={sanitizeText(footer.url)}
          target="_blank"
          rel="noreferrer"
          onClick={() => onTrack && onTrack('media_list_footer', { url: footer.url })}
          className="mt-3 flex items-center justify-center gap-2 rounded-full border border-border dark:border-border-dark py-3 text-sm font-medium text-text-primary dark:text-text-primary-dark transition-colors hover:bg-surface dark:hover:bg-surface-dark"
        >
          {sanitizeText(footer.label)}
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}
